import { lazy, Suspense } from "react";
import { Routes, Route, Link } from "react-router";
import { ProtectedRoute, AuthScreen } from "@itemgo/core";
import { usePushToasts } from "@itemgo/messaging";
import { ErrorBoundary } from "@itemgo/ui";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import ScrollToTop from "@/components/ScrollToTop";
import { AdminDataProvider } from "@/contexts/AdminDataContext";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const UnifiedInbox = lazy(() => import("@/pages/UnifiedInbox"));
const PluginManager = lazy(() => import("@/pages/PluginManager"));
const AdManager = lazy(() => import("@/pages/AdManager"));
const JobAnalytics = lazy(() => import("@/pages/JobAnalytics"));
const PlatformSettings = lazy(() => import("@/pages/PlatformSettings"));
const Subscribers = lazy(() => import("@/pages/Subscribers"));
const Payouts = lazy(() => import("@/pages/Payouts"));
const ClientErrors = lazy(() => import("@/pages/ClientErrors"));
const Messages = lazy(() => import("@/pages/Messages"));
const WaitlistLeads = lazy(() => import("@/pages/WaitlistLeads"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const PushCampaign = lazy(() => import("@/pages/PushCampaign"));
const UserProfile = lazy(() => import("@/pages/UserProfile"));
const BookingOverview = lazy(() => import("@/pages/BookingOverview"));
const BookingDetail = lazy(() => import("@/pages/BookingDetailPage"));
const FinancialDashboard = lazy(() => import("@/pages/FinancialDashboard"));
const PricingManager = lazy(() => import("@/pages/PricingManager"));
const InvoiceManager = lazy(() => import("@/pages/InvoiceManager"));
const VatProfiles = lazy(() => import("@/pages/VatProfiles"));

const ContactMessages = lazy(() => import("@/pages/ContactMessages"));
const ZonesManager = lazy(() => import("@/pages/ZonesManager"));
const DriverMap = lazy(() => import("@/pages/DriverMap"));
const Fleet = lazy(() => import("@/pages/Fleet"));

const TicketManager = lazy(() => import("@/pages/TicketManager"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const EmailCampaign = lazy(() => import("@/pages/EmailCampaign"));
const DeletionRequests = lazy(() => import("@/pages/DeletionRequests"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={28} className="animate-spin text-brand-blue" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h1 className="text-6xl font-bold text-text-primary mb-2">404</h1>
      <p className="text-sm text-text-secondary mb-6">
        The page you are looking for does not exist.
      </p>
      <Link
        to="/"
        className="px-4 py-2 rounded-xl bg-brand-blue text-white text-sm font-medium hover:bg-brand-blue/90 transition-colors"
      >
        Back to Dashboard
      </Link>
    </div>
  );
}

export default function App() {
  usePushToasts();
  return (
    <AdminDataProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <AuthScreen
              portalRole="admin"
              title="ItemGo"
              subtitle="Platform Administration"
              accentColor="bg-brand-blue"
              redirectPath="/"
            />
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AppLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <ScrollToTop />
                    <Routes>
                      <Route index element={<Dashboard />} />
                      <Route path="inbox" element={<UnifiedInbox />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="users/:userId" element={<UserProfile />} />
                      <Route path="push" element={<PushCampaign />} />
                      <Route path="bookings" element={<BookingOverview />} />
                      <Route path="bookings/:id" element={<BookingDetail />} />
                      <Route path="plugins" element={<PluginManager />} />
                      <Route path="ads" element={<AdManager />} />
                      <Route path="jobs" element={<JobAnalytics />} />
                      <Route path="settings" element={<PlatformSettings />} />
                      <Route path="subscribers" element={<Subscribers />} />
                      <Route path="payouts" element={<Payouts />} />
                      <Route path="leads" element={<WaitlistLeads />} />
                      <Route path="accounting" element={<FinancialDashboard />} />
                      <Route path="accounting/pricing" element={<PricingManager />} />
                      <Route path="accounting/invoices" element={<InvoiceManager />} />
                      <Route path="accounting/vat-profiles" element={<VatProfiles />} />
                      <Route path="errors" element={<ClientErrors />} />

                      <Route path="tickets" element={<TicketManager />} />
                      <Route path="messages" element={<Messages />} />
                      <Route path="contact" element={<ContactMessages />} />
                      <Route path="zones" element={<ZonesManager />} />
                      <Route path="drivers-map" element={<DriverMap />} />
                      <Route path="fleet" element={<Fleet />} />
                      <Route path="maintenance" element={<Maintenance />} />
                      <Route path="email" element={<EmailCampaign />} />
                      <Route path="deletions" element={<DeletionRequests />} />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster position="top-center" richColors />
    </AdminDataProvider>
  );
}
